import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { EyeOff, Eye, Pin, FolderPlus, Trash2, X, ChevronDown, ChevronRight, UserMinus, Plus, ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { getAnalysisRecords } from "Explore/Services/Analysis";
import { ANALYSIS_SORT_MAP } from "Explore/Config/constraint";

const parseNum = (str) => {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const num = parseFloat(str.replace(/,/g, '').replace(/\$|B|M|K|%/g, (match) => {
    switch (match) {
      case 'B': return 'e9';
      case 'M': return 'e6';
      case 'K': return 'e3';
      case '%': return 'e-2';
      default: return '';
    }
  }));
  return isNaN(num) ? 0 : num;
};

const formatNum = (num, type) => {
  const safeNum = Number(num);
  if (isNaN(safeNum) || num === null || num === undefined) {
    if (type === 'currency') return '0';
    if (type === 'percent') return '0.00%';
    if (type === 'decimal') return '0.0';
    return '0';
  }
  if (type === 'currency') {
    if (safeNum >= 1e9) return `${(safeNum / 1e9).toFixed(1)}B`;
    if (safeNum >= 1e6) return `${(safeNum / 1e6).toFixed(0)}M`;
    if (safeNum >= 1e3) return `${(safeNum / 1e3).toFixed(0)}K`;
    return `${safeNum}`;
  }
  if (type === 'percent') return `${(safeNum * 100).toFixed(2)}%`;
  if (type === 'decimal') return safeNum.toFixed(1);
  return new Intl.NumberFormat().format(Math.round(safeNum));
};

const StickyScrollTable = ({ children, footer }) => {
  const containerRef = React.useRef(null);
  const fixedWrapperRef = React.useRef(null);
  const ghostRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const outerRef = React.useRef(null);
  const [showGhost, setShowGhost] = React.useState(false);

  React.useEffect(() => {
    const tableScroller = outerRef.current;
    const tableElement = innerRef.current;
    const fixedWrapper = fixedWrapperRef.current;
    const ghost = ghostRef.current;
    
    if (!tableScroller || !tableElement || !ghost || !fixedWrapper) return;

    const onOuterScroll = () => {
      if (ghost.scrollLeft !== tableScroller.scrollLeft) {
        ghost.scrollLeft = tableScroller.scrollLeft;
      }
    };

    const onGhostScroll = () => {
      if (tableScroller.scrollLeft !== ghost.scrollLeft) {
        tableScroller.scrollLeft = ghost.scrollLeft;
      }
    };

    const updateGhostPosition = () => {
      const rect = tableScroller.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // If table bottom is below viewport, and table top is above bottom of viewport
      if (rect.bottom > viewportHeight && rect.top < viewportHeight) {
        // Show fixed footer if there's horizontal overflow OR we just want to pin the pagination
        setShowGhost(true);
        fixedWrapper.style.width = `${rect.width}px`;
        fixedWrapper.style.left = `${rect.left}px`;
        fixedWrapper.style.bottom = '0px';
        
        // Show scrollbar only if horizontal overflow exists
        if (tableScroller.scrollWidth > tableScroller.clientWidth) {
          ghost.style.display = 'block';
          if (ghost.firstChild) {
            ghost.firstChild.style.width = `${tableScroller.scrollWidth}px`;
          }
        } else {
          ghost.style.display = 'none';
        }
      } else {
        setShowGhost(false);
      }
    };

    tableScroller.addEventListener('scroll', onOuterScroll);
    ghost.addEventListener('scroll', onGhostScroll);
    window.addEventListener('scroll', updateGhostPosition, true);
    window.addEventListener('resize', updateGhostPosition);
    
    const ro = new ResizeObserver(updateGhostPosition);
    ro.observe(tableScroller);
    ro.observe(tableElement);
    updateGhostPosition();

    return () => {
      tableScroller.removeEventListener('scroll', onOuterScroll);
      ghost.removeEventListener('scroll', onGhostScroll);
      window.removeEventListener('scroll', updateGhostPosition, true);
      window.removeEventListener('resize', updateGhostPosition);
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Actual scrollable table container */}
      <div
        ref={outerRef}
        style={{ overflowX: 'auto', minHeight: 300 }}
      >
        <div ref={innerRef} style={{ width: 'max-content', minWidth: '100%' }} className="[&>div]:!overflow-visible">
          {children}
        </div>
      </div>
      
      {/* Normal footer in document flow */}
      <div className={showGhost ? 'invisible' : 'visible'}>
        {footer}
      </div>

      {/* Fixed wrapper — sticks to bottom of window when needed */}
      <div
        ref={fixedWrapperRef}
        style={{
          position: 'fixed',
          zIndex: 50,
          display: showGhost ? 'block' : 'none',
          background: 'rgba(255,255,255,1)',
          boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.1), 0 -2px 4px -1px rgba(0,0,0,0.06)',
          borderTop: '1px solid #e2e8f0',
          borderBottomLeftRadius: '0.375rem',
          borderBottomRightRadius: '0.375rem',
        }}
      >
        <div
          ref={ghostRef}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '100%',
          }}
        >
          <div style={{ height: 1 }} />
        </div>
        {footer}
      </div>
    </div>
  );
};



const AnalysisTable = ({ 
  onPinnedChange = () => { }, 
  onRowsChange = () => { },
  onRawRowsChange = () => { },
  onLoadingChange = () => { },
  onSelectedEntitiesChange = () => { },
  filters = {},
  entityType = "institution",
  selectedEntities = [],
  sortConfig: controlledSortConfig,
  onSortChange,
}) => {
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [showOnlyIds, setShowOnlyIds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  
  const entityCacheRef = useRef({});
  const pageCacheRef = useRef({});

  const navigate = useNavigate();
  const [internalSortConfig, setInternalSortConfig] = useState({ key: 'totalPubs', direction: 'desc' });
  const sortConfig = controlledSortConfig !== undefined ? controlledSortConfig : internalSortConfig;
  const setSortConfig = onSortChange || setInternalSortConfig;

  const prevParamsRef = useRef({ filters, entityType, selectedEntities, sortConfig });

  useEffect(() => {
    setSelectedIds([]);
    entityCacheRef.current = {};
    pageCacheRef.current = {};
  }, [entityType]);

  useEffect(() => {
    selectedEntities.forEach(e => {
      if (e && e.id) {
        entityCacheRef.current[e.id] = { id: e.id, name: e.name, type: e.type || entityType };
      }
    });
  }, [selectedEntities, entityType]);

  const [visibleColumns, setVisibleColumns] = useState({
    totalPubs: true,
    pubsPerFaculty: true,
    totalCitations: true,
    citationsPerFaculty: true,
    citationsPerPaper: true,
    hIndex: true,
    h5Index: false,
    h10Index: false,
    i10Index: false,
    gIndex: false,
    medCitPaper: false,
    pctCited: false,
    pctTop10: false,
    pctTop1: false,
    recent5yrShare: false,
    pubCagr: false,
    avgPubsPerYear: false,
    facultyCount: false,
    activeResearchers: false,
    pubsPerActiveRes: false,
    medPubsPerRes: false,
    totalGrant: true,
    totalGrantAmount: false,
    grantPerFaculty: true,
    intCollab: true,
    intCollabCountries: false,
  });

  const INDICATORS = [
    { key: "totalPubs", label: "Total Publications" },
    { key: "pubsPerFaculty", label: "Publications per Faculty" },
    { key: "totalCitations", label: "Total Citations" },
    { key: "citationsPerFaculty", label: "Citation per Faculty" },
    { key: "citationsPerPaper", label: "Citation per Paper" },
    { key: "hIndex", label: "H-Index" },
    { key: "h5Index", label: "H5-Index" },
    { key: "h10Index", label: "H10-Index" },
    { key: "i10Index", label: "i10-Index" },
    { key: "gIndex", label: "G-Index" },
    { key: "medCitPaper", label: "Median Citations / Paper" },
    { key: "pctCited", label: "% Cited Papers" },
    { key: "pctTop10", label: "% Top 10% Cited" },
    { key: "pctTop1", label: "% Top 1% Cited" },
    { key: "recent5yrShare", label: "Recent 5-yr Pub Share" },
    { key: "pubCagr", label: "Publication CAGR" },
    { key: "avgPubsPerYear", label: "Avg Pubs / Year" },
    { key: "medPubsPerRes", label: "Median Pubs / Researcher" },
    { key: "facultyCount", label: "Faculty Count" },
    { key: "activeResearchers", label: "Active Researchers" },
    { key: "pubsPerActiveRes", label: "Pubs / Active Researcher" },
    { key: "totalGrant", label: "Total Grants" },
    { key: "totalGrantAmount", label: "Total Grant Amount ($)" },
    { key: "grantPerFaculty", label: "Grant Amount/ Faculty ($)" },
    { key: "intCollab", label: "International Collaborations" },
    { key: "intCollabCountries", label: "Collaborating Countries" }
  ];

  useEffect(() => {
    const paramsChanged = 
      prevParamsRef.current.filters !== filters ||
      prevParamsRef.current.entityType !== entityType ||
      prevParamsRef.current.selectedEntities !== selectedEntities ||
      prevParamsRef.current.sortConfig !== sortConfig;

    prevParamsRef.current = { filters, entityType, selectedEntities, sortConfig };

    if (paramsChanged) {
      pageCacheRef.current = {};
      if (currentPage !== 0) {
        setCurrentPage(0);
        return;
      }
    }

    const cached = pageCacheRef.current[currentPage];
    if (cached) {
      setRecords(cached.records);
      setTotalCount(cached.total);
      onRowsChange(cached.records);
      onRawRowsChange(cached.rawRecs);
      setIsLoading(false);
      return;
    }

    let ignore = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const backendSortBy = ANALYSIS_SORT_MAP[sortConfig.key] || 'total_pubs';

        const result = await getAnalysisRecords({
          entityType,
          entityIds: selectedEntities.length > 0 ? selectedEntities.map(e => e.id) : undefined,
          yearRange: filters.yearRange,
          subjectArea: filters.subjectArea,
          keywordsOperator: filters.keywordsOperator,
          state: filters.state,
          page: currentPage,
          pageSize: 25,
          sortBy: backendSortBy,
          sortOrder: sortConfig.direction,
        });
        
        if (ignore) return;

        const mapRecords = (recs) => recs.map((r) => {
          const rawIntCollab = r.int_collab;
          const intCollabFormatted = formatNum(rawIntCollab, 'number');

          return {
            id: r.id,
            rank: r.rank,
            name: r.name,
            totalPubs: formatNum(r.total_pubs, 'number'),
            pubsPerFaculty: formatNum(r.pubs_per_faculty, 'decimal'),
            totalCitations: formatNum(r.total_citations, 'number'),
            citationsPerFaculty: formatNum(r.citations_per_faculty, 'number'),
            citationsPerPaper: formatNum(r.citations_per_paper, 'decimal'),
            hIndex: formatNum(r.h_index, 'number'),
            h5Index: formatNum(r.h5_index, 'number'),
            h10Index: formatNum(r.h10_index, 'number'),
            i10Index: formatNum(r.i10_index, 'number'),
            gIndex: formatNum(r.g_index, 'number'),
            pubsPerActiveRes: formatNum(r.pubs_per_active_researcher, "decimal"),
            medCitPaper: formatNum(r.median_citations_per_paper, 'decimal'),
            pctCited: formatNum((r.pct_cited_papers || 0) / 100, 'percent'),
            pctTop10: formatNum((r.pct_top10_cited || 0) / 100, 'percent'),
            pctTop1: formatNum((r.pct_top1_cited || 0) / 100, 'percent'),
            recent5yrShare: formatNum((r.recent_5yr_share || 0) / 100, 'percent'),
            pubCagr: formatNum((r.pub_cagr || 0) / 100, 'percent'),
            avgPubsPerYear: formatNum(r.avg_pubs_per_year, 'decimal'),
            facultyCount: formatNum(r.faculty_count, 'number'),
            activeResearchers: formatNum(r.active_researchers, 'number'),
            medPubsPerRes: formatNum(r.median_pubs_per_researcher, 'decimal'),
            totalGrant: formatNum(r.total_grant, 'number'),
            totalGrantAmount: formatNum(r.total_grant_amount, 'currency'),
            grantPerFaculty: formatNum(r.grant_amount_per_faculty, 'currency'),
            intCollab: formatNum(r.int_collab, 'number'),
            intCollabCountries: formatNum(r.int_collab_countries, 'number'),
          };
        });
        
        const rawRecs = Array.isArray(result.records) ? result.records : (Array.isArray(result) ? result : []);
        const mapped = mapRecords(rawRecs);

        mapped.forEach(r => {
          if (r && r.id) {
            entityCacheRef.current[r.id] = { id: r.id, name: r.name, type: entityType };
          }
        });

        const total = result.total || mapped.length || 0;
        pageCacheRef.current[currentPage] = {
          records: mapped,
          rawRecs,
          total
        };

        setRecords(mapped);
        setTotalCount(total);
        onRowsChange(mapped);
        onRawRowsChange(rawRecs);
      } catch (err) {
        if (ignore) return;
        console.error("fetchData error:", err);
        setError("Failed to load data.");
        setRecords([]);
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, [filters, entityType, selectedEntities, sortConfig, currentPage, onRowsChange, onRawRowsChange]);

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    const pinnedNormalIds = pinnedIds.filter(id => !String(id).startsWith('group-'));
    const pinnedGroups = groups.filter(g => pinnedIds.includes(g.id));
    const pinnedGroupMemberIds = pinnedGroups.flatMap(g => g.memberIds);
    
    const combinedIds = [...new Set([...pinnedNormalIds, ...pinnedGroupMemberIds])];
    const pinnedEntities = combinedIds.map(id => records.find(d => d.id === id) || entityCacheRef.current[id]).filter(Boolean);
    
    onPinnedChange(pinnedEntities);
  }, [pinnedIds, groups, onPinnedChange, records]);

  const isAllCurrentPageSelected = records.length > 0 && records.every(r => selectedIds.includes(r.id));

  const toggleSelectAll = () => {
    if (isAllCurrentPageSelected) {
      const currentPageIds = new Set(records.map(r => r.id));
      setSelectedIds(prev => prev.filter(id => !currentPageIds.has(id)));
    } else {
      records.forEach(r => {
        if (r && r.id) {
          entityCacheRef.current[r.id] = { id: r.id, name: r.name, type: entityType };
        }
      });
      const currentPageIds = records.map(r => r.id);
      setSelectedIds(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const toggleSelect = (id, row) => {
    if (row && row.id && row.name) {
      entityCacheRef.current[row.id] = { id: row.id, name: row.name, type: entityType };
    } else if (!entityCacheRef.current[id]) {
      const rec = records.find(r => r.id === id);
      if (rec) {
        entityCacheRef.current[id] = { id: rec.id, name: rec.name, type: entityType };
      }
    }
    setSelectedIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const handlePinSelected = () => {
    setPinnedIds(prev => [...new Set([...prev, ...selectedIds])].slice(0, 25));
    setSelectedIds([]);
  };

  const handleHideSelected = () => {
    setHiddenIds(prev => [...new Set([...prev, ...selectedIds])]);
    setSelectedIds([]);
  };

  const handleShowOnlySelected = () => {
    setShowOnlyIds(selectedIds);
    setSelectedIds([]);
  };

  const handleCreateGroupClick = () => {
    const members = selectedIds.filter(id => !String(id).startsWith('group-'));
    if (members.length === 0) return;
    setNewGroupName(`Group ${groups.length + 1}`);
    setIsGroupDialogOpen(true);
  };

  const confirmCreateGroup = () => {
    const members = selectedIds.filter(id => !String(id).startsWith('group-'));
    if (!newGroupName.trim() || members.length === 0) return;

    const newGroup = {
      id: `group-${Date.now()}`,
      name: newGroupName.trim(),
      memberIds: members,
      collapsed: false
    };
    
    setGroups(prev => [
      ...prev.map(g => ({
        ...g,
        memberIds: g.memberIds.filter(id => !members.includes(id))
      })).filter(g => g.memberIds.length > 0),
      newGroup
    ]);
    
    setSelectedIds([]);
    setIsGroupDialogOpen(false);
  };

  const handleDeleteGroup = () => {
    setGroups(prev => prev.filter(g => !selectedIds.includes(g.id)));
    setSelectedIds([]);
  };

  const handleRemoveFromGroup = () => {
    setGroups(prev => prev.map(g => ({
      ...g,
      memberIds: g.memberIds.filter(id => !selectedIds.includes(id))
    })).filter(g => g.memberIds.length > 0));
    setSelectedIds([]);
  };

  const handleAddToGroup = (targetGroupId) => {
    const membersToAdd = selectedIds.filter(id => !String(id).startsWith('group-'));
    
    setGroups(prev => prev.map(g => {
      if (g.id === targetGroupId) {
        return {
          ...g,
          memberIds: Array.from(new Set([...g.memberIds, ...membersToAdd]))
        };
      }
      return {
        ...g,
        memberIds: g.memberIds.filter(id => !membersToAdd.includes(id))
      };
    }).filter(g => g.memberIds.length > 0));
    
    setSelectedIds([]);
  };

  const toggleGroupCollapse = (groupId) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, collapsed: !g.collapsed } : g));
  };

  const unpin = (id) => {
    setPinnedIds(prev => prev.filter(p => p !== id));
  };

  const displayData = records.filter(d => {
    const parentGroup = groups.find(g => g.memberIds.includes(d.id));
    const parentGroupId = parentGroup ? parentGroup.id : null;
    
    if (showOnlyIds.length > 0) {
       const isIdShowOnly = showOnlyIds.includes(d.id);
       const isGroupShowOnly = parentGroupId && showOnlyIds.includes(parentGroupId);
       if (!isIdShowOnly && !isGroupShowOnly) return false;
    }
    
    if (hiddenIds.includes(d.id)) return false;
    if (parentGroupId && hiddenIds.includes(parentGroupId)) return false;
    
    return true;
  });

  const aggregateMetrics = (members) => {
    const sum = (key) => members.reduce((acc, m) => acc + parseNum(m[key]), 0);
    const avg = (key) => members.length ? sum(key) / members.length : 0;
    const max = (key) => Math.max(...members.map(m => parseNum(m[key])), 0);

    return {
      totalPubs: formatNum(sum('totalPubs'), 'number'),
      pubsPerFaculty: formatNum(avg('pubsPerFaculty'), 'decimal'),
      totalCitations: formatNum(sum('totalCitations'), 'number'),
      citationsPerFaculty: formatNum(avg('citationsPerFaculty'), 'number'),
      citationsPerPaper: formatNum(avg('citationsPerPaper'), 'decimal'),
      hIndex: formatNum(max('hIndex'), 'number'),
      h5Index: formatNum(max('h5Index'), 'number'),
      h10Index: formatNum(max('h10Index'), 'number'),
      pubsPerActiveRes: formatNum(avg("pubsPerActiveRes"), "decimal"),
      i10Index: formatNum(max('i10Index'), 'number'),
      gIndex: formatNum(max('gIndex'), 'number'),
      medCitPaper: formatNum(avg('medCitPaper'), 'decimal'),
      pctCited: formatNum(avg('pctCited'), 'percent'),
      pctTop10: formatNum(avg('pctTop10'), 'percent'),
      pctTop1: formatNum(avg('pctTop1'), 'percent'),
      recent5yrShare: formatNum(avg('recent5yrShare'), 'percent'),
      pubCagr: formatNum(avg('pubCagr'), 'percent'),
      avgPubsPerYear: formatNum(sum('avgPubsPerYear'), 'decimal'),
      facultyCount: formatNum(sum('facultyCount'), 'number'),
      activeResearchers: formatNum(sum('activeResearchers'), 'number'),
      medPubsPerRes: formatNum(avg('medPubsPerRes'), 'decimal'),
      totalGrant: formatNum(sum('totalGrant'), 'number'),
      totalGrantAmount: formatNum(sum('totalGrantAmount'), 'currency'),
      grantPerFaculty: formatNum(avg('grantPerFaculty'), 'currency'),
      intCollab: formatNum(sum('intCollab'), 'number'),
      intCollabCountries: formatNum(max('intCollabCountries'), 'number')
    };
  };

  const sortedDisplayData = React.useMemo(() => {
    let sortableItems = [...displayData];
    sortableItems.sort((a, b) => {
      let valA = parseNum(a[sortConfig.key]);
      let valB = parseNum(b[sortConfig.key]);
      if (sortConfig.key === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortableItems;
  }, [displayData, sortConfig]);

  const pinnedNormalIds = pinnedIds.filter(id => !String(id).startsWith('group-'));
  const pinnedGroupIds = pinnedIds.filter(id => typeof id === 'string');
  
  const pinnedNormalData = pinnedNormalIds.map(id => displayData.find(d => d.id === id)).filter(Boolean);
  const pinnedGroups = groups.filter(g => pinnedGroupIds.includes(g.id));
  const unpinnedGroupsRaw = groups.filter(g => !pinnedGroupIds.includes(g.id));

  const unpinnedData = sortedDisplayData.filter(d => !pinnedNormalIds.includes(d.id));
  
  const allGroupMemberIds = groups.flatMap(g => g.memberIds);
  const ungroupedData = unpinnedData.filter(d => !allGroupMemberIds.includes(d.id));

  const sortedUnpinnedGroups = React.useMemo(() => {
    let groupsWithAgg = unpinnedGroupsRaw.map(group => {
      const members = unpinnedData.filter(d => group.memberIds.includes(d.id));
      const agg = aggregateMetrics(members);
      return { ...group, agg, members };
    }).filter(g => g.members.length > 0);
    
    groupsWithAgg.sort((a, b) => {
      let valA = parseNum(a.agg[sortConfig.key] || 0);
      let valB = parseNum(b.agg[sortConfig.key] || 0);
      if (sortConfig.key === 'name') {
        valA = a.name.toLowerCase();
        valB = b.name.toLowerCase();
      }
      if (sortConfig.key === 'rank') {
         valA = Math.min(...a.members.map(m => m.rank));
         valB = Math.min(...b.members.map(m => m.rank));
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return groupsWithAgg;
  }, [unpinnedGroupsRaw, unpinnedData, sortConfig]);

  const getCellClass = (key, baseClass, activeClass = "font-bold text-slate-900") => {
    return `text-center whitespace-nowrap ${sortConfig.key === key ? activeClass : baseClass}`;
  };

  const getFilterBannerText = () => {
    let text = '';
    
    if (showOnlyIds.length > 0) {
      const showGroups = showOnlyIds.filter(id => typeof id === 'string').length;
      const showNormal = showOnlyIds.filter(id => !String(id).startsWith('group-')).length;
      const parts = [];
      if (showGroups > 0) parts.push(`${showGroups} group${showGroups > 1 ? 's' : ''}`);
      if (showNormal > 0) parts.push(`${showNormal} organization${showNormal > 1 ? 's' : ''}`);
      text += `Showing only ${parts.join(' and ')}. `;
    }
    
    if (hiddenIds.length > 0) {
      const hiddenGroups = hiddenIds.filter(id => typeof id === 'string').length;
      const hiddenNormal = hiddenIds.filter(id => !String(id).startsWith('group-')).length;
      const parts = [];
      if (hiddenGroups > 0) parts.push(`${hiddenGroups} group${hiddenGroups > 1 ? 's' : ''}`);
      if (hiddenNormal > 0) parts.push(`${hiddenNormal} organization${hiddenNormal > 1 ? 's' : ''}`);
      
      const isPlural = hiddenGroups + hiddenNormal > 1 || (hiddenGroups === 0 && hiddenNormal > 1) || (hiddenNormal === 0 && hiddenGroups > 1);
      // Wait, parts.length > 1 is definitely plural.
      // If parts == 1, check the number.
      text += `${parts.join(' and ')} ${parts.length > 1 || hiddenGroups > 1 || hiddenNormal > 1 ? 'are' : 'is'} hidden.`;
    }
    
    return text.trim();
  };

  const renderGroup = (group, isPinned, precalcMembers = null, precalcAgg = null) => {
    const dataSource = isPinned ? sortedDisplayData : unpinnedData;
    const members = precalcMembers || dataSource.filter(d => group.memberIds.includes(d.id));
    
    if (members.length === 0) return null;
    const agg = precalcAgg || aggregateMetrics(members);
    
    return (
      <React.Fragment key={`group-${group.id}-${isPinned ? 'pinned' : 'unpinned'}`}>
        {/* Group Header */}
        <TableRow className={`bg-slate-100 hover:bg-slate-200 border-y border-slate-300 ${isPinned ? 'bg-white' : ''}`}>
          <TableCell className="text-center whitespace-nowrap">
            <input
              type="checkbox"
              className="w-4 h-4 cursor-pointer"
              checked={selectedIds.includes(group.id)}
              onChange={() => toggleSelect(group.id)}
            />
          </TableCell>
          <TableCell className="text-center whitespace-nowrap">
            {isPinned ? (
              <button onClick={() => unpin(group.id)} className="text-explore hover:text-explore/80">
                <X size={16} />
              </button>
            ) : "-"}
          </TableCell>
          <TableCell className="whitespace-nowrap flex items-center gap-2">
            <button onClick={() => toggleGroupCollapse(group.id)} className="text-slate-500 hover:text-slate-700">
              {group.collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </button>
            <span className="font-bold text-slate-800">{group.name}</span>
            <span className="text-xs text-slate-500">({members.length})</span>
          </TableCell>
          {visibleColumns.totalPubs && <TableCell className={getCellClass('totalPubs', 'font-medium text-slate-800')}>{agg.totalPubs}</TableCell>}
          {visibleColumns.pubsPerFaculty && <TableCell className={getCellClass('pubsPerFaculty', 'text-slate-700 font-medium')}>{agg.pubsPerFaculty}</TableCell>}
          {visibleColumns.totalCitations && <TableCell className={getCellClass('totalCitations', 'text-slate-700 font-medium')}>{agg.totalCitations}</TableCell>}
          {visibleColumns.citationsPerFaculty && <TableCell className={getCellClass('citationsPerFaculty', 'text-slate-700 font-medium')}>{agg.citationsPerFaculty}</TableCell>}
          {visibleColumns.citationsPerPaper && <TableCell className={getCellClass('citationsPerPaper', 'text-slate-700 font-medium')}>{agg.citationsPerPaper}</TableCell>}
          {visibleColumns.hIndex && <TableCell className={getCellClass('hIndex', 'text-slate-700 font-medium')}>{agg.hIndex}</TableCell>}
          {visibleColumns.h5Index && <TableCell className={getCellClass('h5Index', 'text-slate-700 font-medium')}>{agg.h5Index}</TableCell>}
          {visibleColumns.h10Index && <TableCell className={getCellClass('h10Index', 'text-slate-700 font-medium')}>{agg.h10Index}</TableCell>}
          {visibleColumns.i10Index && <TableCell className={getCellClass('i10Index', 'text-slate-700 font-medium')}>{agg.i10Index}</TableCell>}
          {visibleColumns.gIndex && <TableCell className={getCellClass('gIndex', 'text-slate-700 font-medium')}>{agg.gIndex}</TableCell>}
          {visibleColumns.medCitPaper && <TableCell className={getCellClass('medCitPaper', 'text-slate-700 font-medium')}>{agg.medCitPaper}</TableCell>}
          {visibleColumns.pctCited && <TableCell className={getCellClass('pctCited', 'text-slate-700 font-medium')}>{agg.pctCited}</TableCell>}
          {visibleColumns.pctTop10 && <TableCell className={getCellClass('pctTop10', 'text-slate-700 font-medium')}>{agg.pctTop10}</TableCell>}
          {visibleColumns.pctTop1 && <TableCell className={getCellClass('pctTop1', 'text-slate-700 font-medium')}>{agg.pctTop1}</TableCell>}
          {visibleColumns.recent5yrShare && <TableCell className={getCellClass('recent5yrShare', 'text-slate-700 font-medium')}>{agg.recent5yrShare}</TableCell>}
          {visibleColumns.pubCagr && <TableCell className={getCellClass('pubCagr', 'text-slate-700 font-medium')}>{agg.pubCagr}</TableCell>}
          {visibleColumns.avgPubsPerYear && <TableCell className={getCellClass('avgPubsPerYear', 'text-slate-700 font-medium')}>{agg.avgPubsPerYear}</TableCell>}
          {visibleColumns.facultyCount && <TableCell className={getCellClass('facultyCount', 'text-slate-700 font-medium')}>{agg.facultyCount}</TableCell>}
          {visibleColumns.activeResearchers && <TableCell className={getCellClass('activeResearchers', 'text-slate-700 font-medium')}>{agg.activeResearchers}</TableCell>}
          {visibleColumns.pubsPerActiveRes && <TableCell className={getCellClass('pubsPerActiveRes', 'text-slate-700 font-medium')}>{agg.pubsPerActiveRes}</TableCell>}
          {visibleColumns.medPubsPerRes && <TableCell className={getCellClass('medPubsPerRes', 'text-slate-700 font-medium')}>{agg.medPubsPerRes}</TableCell>}
          {visibleColumns.totalGrant && <TableCell className={getCellClass('totalGrant', 'text-slate-700 font-medium')}>{agg.totalGrant}</TableCell>}
          {visibleColumns.totalGrantAmount && <TableCell className={getCellClass('totalGrantAmount', 'text-slate-700 font-medium')}>{agg.totalGrantAmount}</TableCell>}
          {visibleColumns.grantPerFaculty && <TableCell className={getCellClass('grantPerFaculty', 'text-slate-700 font-medium')}>{agg.grantPerFaculty}</TableCell>}
          {visibleColumns.intCollab && <TableCell className={getCellClass('intCollab', 'text-slate-700 font-medium')}>{agg.intCollab}</TableCell>}
          {visibleColumns.intCollabCountries && <TableCell className={getCellClass('intCollabCountries', 'text-slate-700 font-medium')}>{agg.intCollabCountries}</TableCell>}
        </TableRow>
        {/* Group Members */}
        {!group.collapsed && members.map((row, idx) => (
          <TableRow key={row.id} className="hover:bg-slate-100/50 bg-slate-50/60 border-l-4 border-l-explore/40 border-y-0">
            <TableCell className="text-center whitespace-nowrap border-r border-slate-200/50">
              <input
                type="checkbox"
                className="w-4 h-4 cursor-pointer"
                checked={selectedIds.includes(row.id)}
                onChange={() => toggleSelect(row.id, row)}
              />
            </TableCell>
            <TableCell className="text-center whitespace-nowrap text-slate-500">{row.rank}</TableCell>
            <TableCell className="whitespace-nowrap pl-10 relative">
              <div className={`absolute left-4 w-[1.5px] bg-slate-300 ${idx === members.length - 1 ? 'top-0 bottom-1/2' : 'top-0 bottom-0'}`}></div>
              <div className="absolute left-4 top-1/2 w-4 h-[1.5px] bg-slate-300"></div>
              <Button 
                variant="link" 
                className="p-0 h-auto font-medium text-slate-600 hover:opacity-80 relative z-10"
                onClick={() => {
                  navigate(`/explore/analysis/${entityType}/${row.id}`);
                }}
              >
                {row.name}
              </Button>
            </TableCell>
            {visibleColumns.totalPubs && <TableCell className={getCellClass('totalPubs', 'text-slate-600')}>{row.totalPubs}</TableCell>}
            {visibleColumns.pubsPerFaculty && <TableCell className={getCellClass('pubsPerFaculty', 'text-slate-500')}>{row.pubsPerFaculty}</TableCell>}
            {visibleColumns.totalCitations && <TableCell className={getCellClass('totalCitations', 'text-slate-500')}>{row.totalCitations}</TableCell>}
            {visibleColumns.citationsPerFaculty && <TableCell className={getCellClass('citationsPerFaculty', 'text-slate-500')}>{row.citationsPerFaculty}</TableCell>}
            {visibleColumns.citationsPerPaper && <TableCell className={getCellClass('citationsPerPaper', 'text-slate-500')}>{row.citationsPerPaper}</TableCell>}
            {visibleColumns.hIndex && <TableCell className={getCellClass('hIndex', 'text-slate-600')}>{row.hIndex}</TableCell>}
            {visibleColumns.h5Index && <TableCell className={getCellClass('h5Index', 'text-slate-600')}>{row.h5Index}</TableCell>}
            {visibleColumns.h10Index && <TableCell className={getCellClass('h10Index', 'text-slate-600')}>{row.h10Index}</TableCell>}
            {visibleColumns.i10Index && <TableCell className={getCellClass('i10Index', 'text-slate-600')}>{row.i10Index}</TableCell>}
            {visibleColumns.gIndex && <TableCell className={getCellClass('gIndex', 'text-slate-600')}>{row.gIndex}</TableCell>}
            {visibleColumns.medCitPaper && <TableCell className={getCellClass('medCitPaper', 'text-slate-600')}>{row.medCitPaper}</TableCell>}
            {visibleColumns.pctCited && <TableCell className={getCellClass('pctCited', 'text-slate-600')}>{row.pctCited}</TableCell>}
            {visibleColumns.pctTop10 && <TableCell className={getCellClass('pctTop10', 'text-slate-600')}>{row.pctTop10}</TableCell>}
            {visibleColumns.pctTop1 && <TableCell className={getCellClass('pctTop1', 'text-slate-600')}>{row.pctTop1}</TableCell>}
            {visibleColumns.recent5yrShare && <TableCell className={getCellClass('recent5yrShare', 'text-slate-600')}>{row.recent5yrShare}</TableCell>}
            {visibleColumns.pubCagr && <TableCell className={getCellClass('pubCagr', 'text-slate-600')}>{row.pubCagr}</TableCell>}
            {visibleColumns.avgPubsPerYear && <TableCell className={getCellClass('avgPubsPerYear', 'text-slate-600')}>{row.avgPubsPerYear}</TableCell>}
            {visibleColumns.facultyCount && <TableCell className={getCellClass('facultyCount', 'text-slate-600')}>{row.facultyCount}</TableCell>}
            {visibleColumns.activeResearchers && <TableCell className={getCellClass('activeResearchers', 'text-slate-600')}>{row.activeResearchers}</TableCell>}
            {visibleColumns.pubsPerActiveRes && <TableCell className={getCellClass('pubsPerActiveRes', 'text-slate-600')}>{row.pubsPerActiveRes}</TableCell>}
            {visibleColumns.medPubsPerRes && <TableCell className={getCellClass('medPubsPerRes', 'text-slate-600')}>{row.medPubsPerRes}</TableCell>}
            {visibleColumns.totalGrant && <TableCell className={getCellClass('totalGrant', 'text-slate-500')}>{row.totalGrant}</TableCell>}
            {visibleColumns.totalGrantAmount && <TableCell className={getCellClass('totalGrantAmount', 'text-slate-500')}>{row.totalGrantAmount}</TableCell>}
            {visibleColumns.grantPerFaculty && <TableCell className={getCellClass('grantPerFaculty', 'text-slate-500')}>{row.grantPerFaculty}</TableCell>}
            {visibleColumns.intCollab && <TableCell className={getCellClass('intCollab', 'text-slate-500')}>{row.intCollab}</TableCell>}
            {visibleColumns.intCollabCountries && <TableCell className={getCellClass('intCollabCountries', 'text-slate-500')}>{row.intCollabCountries}</TableCell>}
          </TableRow>
        ))}
      </React.Fragment>
    );
  };

  const paginationFooter = (
    <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
      <div className="text-sm text-slate-500">
        Showing {totalCount === 0 ? 0 : Math.min(currentPage * 25 + 1, totalCount)} to {Math.min((currentPage + 1) * 25, totalCount)} of {new Intl.NumberFormat().format(totalCount)} results
      </div>
      <div className="flex items-center space-x-1">
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8 w-8 p-0 text-slate-600" 
          disabled={currentPage === 0 || isLoading}
          onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
        >
          &lt;
        </Button>
        
        {(() => {
          const totalPages = Math.ceil(totalCount / 25);
          if (totalPages <= 0) return null;
          
          const pages = [];
          if (totalPages <= 7) {
            for (let i = 0; i < totalPages; i++) pages.push(i);
          } else {
            if (currentPage <= 3) {
              pages.push(0, 1, 2, 3, 4, '...', totalPages - 1);
            } else if (currentPage >= totalPages - 4) {
              pages.push(0, '...', totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
            } else {
              pages.push(0, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages - 1);
            }
          }

          return pages.map((p, idx) => (
            p === '...' ? (
              <span key={`dots-${idx}`} className="px-1 text-slate-400">...</span>
            ) : (
              <Button 
                key={p}
                variant="outline" 
                size="sm" 
                onClick={() => setCurrentPage(p)}
                className={`h-8 min-w-[32px] px-2 p-0 ${p === currentPage ? "bg-explore text-white hover:bg-explore/90 hover:text-white border-explore" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}
              >
                {p + 1}
              </Button>
            )
          ));
        })()}
        
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8 w-8 p-0 text-slate-600" 
          disabled={(currentPage + 1) * 25 >= totalCount || isLoading}
          onClick={() => setCurrentPage(p => p + 1)}
        >
          &gt;
        </Button>
      </div>
    </div>
  );

  return (
    <div className="w-full flex flex-col gap-4 relative">
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-explore" />
            <span className="font-medium text-slate-700">Loading analysis data...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded-md">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-500 font-medium">
        <span>{new Intl.NumberFormat().format(totalCount)} {entityType === 'researcher' ? 'researchers' : entityType === 'unit' ? 'units' : 'organizations'}</span>
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-2 text-slate-600 font-medium">
                <Plus size={14} /> Add indicator
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border border-slate-200">
              <div className="p-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Select Indicators</div>
              {INDICATORS.map(ind => (
                <DropdownMenuItem key={ind.key} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer" onClick={(e) => {
                  e.preventDefault();
                  setVisibleColumns(prev => ({...prev, [ind.key]: !prev[ind.key]}));
                }}>
                  <input type="checkbox" checked={visibleColumns[ind.key]} readOnly className="w-4 h-4 cursor-pointer" />
                  <span className="text-sm font-medium text-slate-700">{ind.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {(hiddenIds.length > 0 || showOnlyIds.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-2 text-sm flex items-center justify-between">
          <span>
            {getFilterBannerText()}
          </span>
          <button
            onClick={() => { setHiddenIds([]); setShowOnlyIds([]); }}
            className="text-amber-900 font-semibold hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Helper functions for header rendering */}
      {(() => {
        const handleSort = (key) => {
          let direction = 'desc';
          if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
          }
          setSortConfig({ key, direction });
        };

        const renderSortableHeader = (label, key, className = "") => {
          return (
            <TableHead 
              className={`whitespace-nowrap font-medium cursor-pointer select-none hover:bg-slate-100 transition-colors group ${className}`} 
              onClick={() => handleSort(key)}
            >
              <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
                <span>{label}</span>
                {sortConfig.key === key ? (
                  sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-explore" /> : <ArrowDown className="w-3 h-3 text-explore" />
                ) : (
                  <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100" />
                )}
              </div>
            </TableHead>
          );
        };

        return (
          <div className="border border-slate-200 rounded-md bg-white">
            <StickyScrollTable footer={paginationFooter}>
              <Table className="text-sm">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[40px] text-center whitespace-nowrap font-medium">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 cursor-pointer" 
                        checked={isAllCurrentPageSelected} 
                        ref={el => {
                          if (el) {
                            el.indeterminate = !isAllCurrentPageSelected && records.some(r => selectedIds.includes(r.id));
                          }
                        }}
                        onChange={toggleSelectAll} 
                      />
                    </TableHead>
                    <TableHead className="whitespace-nowrap font-medium text-center">Rank</TableHead>
                    <TableHead className="whitespace-nowrap font-medium min-w-[250px]">Organization Name</TableHead>
                    {visibleColumns.totalPubs && renderSortableHeader(<>Total<br />Publications</>, "totalPubs", "text-center")}
                    {visibleColumns.pubsPerFaculty && renderSortableHeader(<>Publications<br />per Faculty</>, "pubsPerFaculty", "text-center")}
                    {visibleColumns.totalCitations && renderSortableHeader(<>Total<br />Citations</>, "totalCitations", "text-center")}
                    {visibleColumns.citationsPerFaculty && renderSortableHeader(<>Citation<br />per Faculty</>, "citationsPerFaculty", "text-center")}
                    {visibleColumns.citationsPerPaper && renderSortableHeader(<>Citation<br />per Paper</>, "citationsPerPaper", "text-center")}
                    {visibleColumns.hIndex && renderSortableHeader("H-Index", "hIndex", "text-center")}
                    {visibleColumns.h5Index && renderSortableHeader("H5-Index", "h5Index", "text-center")}
                    {visibleColumns.h10Index && renderSortableHeader("H10-Index", "h10Index", "text-center")}
                    {visibleColumns.i10Index && renderSortableHeader("i10-Index", "i10Index", "text-center")}
                    {visibleColumns.gIndex && renderSortableHeader("G-Index", "gIndex", "text-center")}
                    {visibleColumns.medCitPaper && renderSortableHeader(<>Median Citations<br />per Paper</>, "medCitPaper", "text-center")}
                    {visibleColumns.pctCited && renderSortableHeader(<>% Cited<br />Papers</>, "pctCited", "text-center")}
                    {visibleColumns.pctTop10 && renderSortableHeader(<>% in Top 10%<br />Cited</>, "pctTop10", "text-center")}
                    {visibleColumns.pctTop1 && renderSortableHeader(<>% in Top 1%<br />Cited</>, "pctTop1", "text-center")}
                    {visibleColumns.recent5yrShare && renderSortableHeader(<>Recent 5-yr<br />Pub Share</>, "recent5yrShare", "text-center")}
                    {visibleColumns.pubCagr && renderSortableHeader(<>Publication<br />CAGR</>, "pubCagr", "text-center")}
                    {visibleColumns.avgPubsPerYear && renderSortableHeader(<>Avg Pubs<br />per Year</>, "avgPubsPerYear", "text-center")}
                    {visibleColumns.facultyCount && renderSortableHeader(<>Faculty<br />Count</>, "facultyCount", "text-center")}
                    {visibleColumns.activeResearchers && renderSortableHeader(<>Active<br />Researchers</>, "activeResearchers", "text-center")}
                    {visibleColumns.pubsPerActiveRes && renderSortableHeader(<>Pubs per Active<br />Researcher</>, "pubsPerActiveRes", "text-center")}
                    {visibleColumns.medPubsPerRes && renderSortableHeader(<>Median Pubs<br />per Researcher</>, "medPubsPerRes", "text-center")}
                    {visibleColumns.totalGrant && renderSortableHeader(<>Total<br />Grants</>, "totalGrant", "text-center")}
                    {visibleColumns.totalGrantAmount && renderSortableHeader(<>Total Grant<br />Amount ($)</>, "totalGrantAmount", "text-center")}
                    {visibleColumns.grantPerFaculty && renderSortableHeader(<>Grant Amount/<br />Faculty ($)</>, "grantPerFaculty", "text-center")}
                    {visibleColumns.intCollab && renderSortableHeader(<>International<br />Collaborations</>, "intCollab", "text-center")}
                    {visibleColumns.intCollabCountries && renderSortableHeader(<>Collaborating<br />Countries</>, "intCollabCountries", "text-center")}
                  </TableRow>
                </TableHeader>
            <TableBody>
              {/* Pinned Normal Rows */}
              {pinnedNormalData.map((row) => (
                <TableRow key={`pinned-${row.id}`} className="bg-white hover:bg-slate-50">
                  <TableCell className="text-center whitespace-nowrap">
                    <button onClick={() => unpin(row.id)} className="text-explore hover:text-explore/80">
                      <X size={16} />
                    </button>
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">{row.rank}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Button variant="link" className="p-0 h-auto font-semibold text-explore hover:opacity-80">
                      {row.name}
                    </Button>
                  </TableCell>
                  {visibleColumns.totalPubs && <TableCell className={getCellClass('totalPubs', 'text-explore/80', 'font-bold text-explore')}>{row.totalPubs}</TableCell>}
                  {visibleColumns.pubsPerFaculty && <TableCell className={getCellClass('pubsPerFaculty', 'text-explore/80', 'font-bold text-explore')}>{row.pubsPerFaculty}</TableCell>}
                  {visibleColumns.totalCitations && <TableCell className={getCellClass('totalCitations', 'text-explore/80', 'font-bold text-explore')}>{row.totalCitations}</TableCell>}
                  {visibleColumns.citationsPerFaculty && <TableCell className={getCellClass('citationsPerFaculty', 'text-explore/80', 'font-bold text-explore')}>{row.citationsPerFaculty}</TableCell>}
                  {visibleColumns.citationsPerPaper && <TableCell className={getCellClass('citationsPerPaper', 'text-explore/80', 'font-bold text-explore')}>{row.citationsPerPaper}</TableCell>}
                  {visibleColumns.hIndex && <TableCell className={getCellClass('hIndex', 'text-explore/80', 'font-bold text-explore')}>{row.hIndex}</TableCell>}
                  {visibleColumns.h5Index && <TableCell className={getCellClass('h5Index', 'text-explore/80', 'font-bold text-explore')}>{row.h5Index}</TableCell>}
                  {visibleColumns.h10Index && <TableCell className={getCellClass('h10Index', 'text-explore/80', 'font-bold text-explore')}>{row.h10Index}</TableCell>}
                  {visibleColumns.i10Index && <TableCell className={getCellClass('i10Index', 'text-explore/80', 'font-bold text-explore')}>{row.i10Index}</TableCell>}
                  {visibleColumns.gIndex && <TableCell className={getCellClass('gIndex', 'text-explore/80', 'font-bold text-explore')}>{row.gIndex}</TableCell>}
                  {visibleColumns.medCitPaper && <TableCell className={getCellClass('medCitPaper', 'text-explore/80', 'font-bold text-explore')}>{row.medCitPaper}</TableCell>}
                  {visibleColumns.pctCited && <TableCell className={getCellClass('pctCited', 'text-explore/80', 'font-bold text-explore')}>{row.pctCited}</TableCell>}
                  {visibleColumns.pctTop10 && <TableCell className={getCellClass('pctTop10', 'text-explore/80', 'font-bold text-explore')}>{row.pctTop10}</TableCell>}
                  {visibleColumns.pctTop1 && <TableCell className={getCellClass('pctTop1', 'text-explore/80', 'font-bold text-explore')}>{row.pctTop1}</TableCell>}
                  {visibleColumns.recent5yrShare && <TableCell className={getCellClass('recent5yrShare', 'text-explore/80', 'font-bold text-explore')}>{row.recent5yrShare}</TableCell>}
                  {visibleColumns.pubCagr && <TableCell className={getCellClass('pubCagr', 'text-explore/80', 'font-bold text-explore')}>{row.pubCagr}</TableCell>}
                  {visibleColumns.avgPubsPerYear && <TableCell className={getCellClass('avgPubsPerYear', 'text-explore/80', 'font-bold text-explore')}>{row.avgPubsPerYear}</TableCell>}
                  {visibleColumns.facultyCount && <TableCell className={getCellClass('facultyCount', 'text-explore/80', 'font-bold text-explore')}>{row.facultyCount}</TableCell>}
                  {visibleColumns.activeResearchers && <TableCell className={getCellClass('activeResearchers', 'text-explore/80', 'font-bold text-explore')}>{row.activeResearchers}</TableCell>}
                  {visibleColumns.pubsPerActiveRes && <TableCell className={getCellClass('pubsPerActiveRes', 'text-explore/80', 'font-bold text-explore')}>{row.pubsPerActiveRes}</TableCell>}
                  {visibleColumns.medPubsPerRes && <TableCell className={getCellClass('medPubsPerRes', 'text-explore/80', 'font-bold text-explore')}>{row.medPubsPerRes}</TableCell>}
                  {visibleColumns.totalGrant && <TableCell className={getCellClass('totalGrant', 'text-explore/80', 'font-bold text-explore')}>{row.totalGrant}</TableCell>}
                  {visibleColumns.totalGrantAmount && <TableCell className={getCellClass('totalGrantAmount', 'text-explore/80', 'font-bold text-explore')}>{row.totalGrantAmount}</TableCell>}
                  {visibleColumns.grantPerFaculty && <TableCell className={getCellClass('grantPerFaculty', 'text-explore/80', 'font-bold text-explore')}>{row.grantPerFaculty}</TableCell>}
                  {visibleColumns.intCollab && <TableCell className={getCellClass('intCollab', 'text-explore/80', 'font-bold text-explore')}>{row.intCollab}</TableCell>}
                  {visibleColumns.intCollabCountries && <TableCell className={getCellClass('intCollabCountries', 'text-explore/80', 'font-bold text-explore')}>{row.intCollabCountries}</TableCell>}
                </TableRow>
              ))}

              {/* Pinned Groups */}
              {pinnedGroups.map(group => renderGroup(group, true))}

              {/* Separator if there are pinned items */}
              {(pinnedNormalData.length > 0 || pinnedGroups.length > 0) && (
                <TableRow className="bg-slate-50 border-y border-slate-200">
                  <TableCell colSpan={3 + Object.values(visibleColumns).filter(Boolean).length} className="py-2 px-4 text-xs font-medium text-slate-500">
                    <div className="flex justify-between items-center w-full max-w-[250px]">
                      <span>{pinnedNormalData.length + pinnedGroups.length} items pinned</span>
                      <button onClick={() => setPinnedIds([])} className="hover:underline text-slate-400 hover:text-slate-600">Remove all</button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {/* Unpinned Groups */}
              {sortedUnpinnedGroups.map(group => renderGroup(group, false, group.members, group.agg))}

              {/* Ungrouped Rows */}
              {ungroupedData.map((row) => (
                <TableRow key={row.id} className="hover:bg-slate-50">
                  <TableCell className="text-center whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="w-4 h-4 cursor-pointer"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id, row)}
                    />
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">{row.rank}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Button 
                      variant="link" 
                      className="p-0 h-auto font-semibold text-explore hover:opacity-80 relative z-10"
                      onClick={() => {
                        navigate(`/explore/analysis/${entityType}/${row.id}`);
                      }}
                    >
                      {row.name}
                    </Button>
                  </TableCell>
                  {visibleColumns.totalPubs && <TableCell className={getCellClass('totalPubs', 'text-slate-600')}>{row.totalPubs}</TableCell>}
                  {visibleColumns.pubsPerFaculty && <TableCell className={getCellClass('pubsPerFaculty', 'text-slate-600')}>{row.pubsPerFaculty}</TableCell>}
                  {visibleColumns.totalCitations && <TableCell className={getCellClass('totalCitations', 'text-slate-600')}>{row.totalCitations}</TableCell>}
                  {visibleColumns.citationsPerFaculty && <TableCell className={getCellClass('citationsPerFaculty', 'text-slate-600')}>{row.citationsPerFaculty}</TableCell>}
                  {visibleColumns.citationsPerPaper && <TableCell className={getCellClass('citationsPerPaper', 'text-slate-600')}>{row.citationsPerPaper}</TableCell>}
                  {visibleColumns.hIndex && <TableCell className={getCellClass('hIndex', 'text-slate-600')}>{row.hIndex}</TableCell>}
                  {visibleColumns.h5Index && <TableCell className={getCellClass('h5Index', 'text-slate-600')}>{row.h5Index}</TableCell>}
                  {visibleColumns.h10Index && <TableCell className={getCellClass('h10Index', 'text-slate-600')}>{row.h10Index}</TableCell>}
                  {visibleColumns.i10Index && <TableCell className={getCellClass('i10Index', 'text-slate-600')}>{row.i10Index}</TableCell>}
                  {visibleColumns.gIndex && <TableCell className={getCellClass('gIndex', 'text-slate-600')}>{row.gIndex}</TableCell>}
                  {visibleColumns.medCitPaper && <TableCell className={getCellClass('medCitPaper', 'text-slate-600')}>{row.medCitPaper}</TableCell>}
                  {visibleColumns.pctCited && <TableCell className={getCellClass('pctCited', 'text-slate-600')}>{row.pctCited}</TableCell>}
                  {visibleColumns.pctTop10 && <TableCell className={getCellClass('pctTop10', 'text-slate-600')}>{row.pctTop10}</TableCell>}
                  {visibleColumns.pctTop1 && <TableCell className={getCellClass('pctTop1', 'text-slate-600')}>{row.pctTop1}</TableCell>}
                  {visibleColumns.recent5yrShare && <TableCell className={getCellClass('recent5yrShare', 'text-slate-600')}>{row.recent5yrShare}</TableCell>}
                  {visibleColumns.pubCagr && <TableCell className={getCellClass('pubCagr', 'text-slate-600')}>{row.pubCagr}</TableCell>}
                  {visibleColumns.avgPubsPerYear && <TableCell className={getCellClass('avgPubsPerYear', 'text-slate-600')}>{row.avgPubsPerYear}</TableCell>}
                  {visibleColumns.facultyCount && <TableCell className={getCellClass('facultyCount', 'text-slate-600')}>{row.facultyCount}</TableCell>}
                  {visibleColumns.activeResearchers && <TableCell className={getCellClass('activeResearchers', 'text-slate-600')}>{row.activeResearchers}</TableCell>}
                  {visibleColumns.pubsPerActiveRes && <TableCell className={getCellClass('pubsPerActiveRes', 'text-slate-600')}>{row.pubsPerActiveRes}</TableCell>}
                  {visibleColumns.medPubsPerRes && <TableCell className={getCellClass('medPubsPerRes', 'text-slate-600')}>{row.medPubsPerRes}</TableCell>}
                  {visibleColumns.totalGrant && <TableCell className={getCellClass('totalGrant', 'text-slate-600')}>{row.totalGrant}</TableCell>}
                  {visibleColumns.totalGrantAmount && <TableCell className={getCellClass('totalGrantAmount', 'text-slate-600')}>{row.totalGrantAmount}</TableCell>}
                  {visibleColumns.grantPerFaculty && <TableCell className={getCellClass('grantPerFaculty', 'text-slate-600')}>{row.grantPerFaculty}</TableCell>}
                  {visibleColumns.intCollab && <TableCell className={getCellClass('intCollab', 'text-slate-600')}>{row.intCollab}</TableCell>}
                  {visibleColumns.intCollabCountries && <TableCell className={getCellClass('intCollabCountries', 'text-slate-600')}>{row.intCollabCountries}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StickyScrollTable>
      </div>
      );
    })()}

    {/* Floating Action Bar */}
      {selectedIds.length > 0 && (() => {
        const selectedEntityIds = selectedIds.flatMap(id => {
          if (String(id).startsWith('group-')) {
            const group = groups.find(g => g.id === id);
            return group ? group.memberIds : [];
          }
          return [id];
        });
        const uniqueEntityIds = [...new Set(selectedEntityIds)];
        const count = uniqueEntityIds.length || selectedIds.length;
        const entityLabel = entityType === 'researcher' 
          ? (count > 1 ? 'researchers' : 'researcher')
          : entityType === 'unit'
          ? (count > 1 ? 'units' : 'unit')
          : (count > 1 ? 'organizations' : 'organization');

        return (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#0f172a] text-white rounded-full shadow-2xl flex items-center px-4 py-2 gap-2 border border-slate-700/50">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 rounded-full text-white hover:text-white hover:bg-slate-800 text-sm font-medium px-4 gap-2" 
              onClick={() => {
                const newEntities = uniqueEntityIds.map(id => {
                  if (entityCacheRef.current[id]) {
                    return entityCacheRef.current[id];
                  }
                  const record = records.find(r => r.id === id);
                  if (record) {
                    return { id: record.id, name: record.name, type: entityType };
                  }
                  return null;
                }).filter(Boolean);

                setCurrentPage(0);
                setSelectedIds([]);
                pageCacheRef.current = {};
                onSelectedEntitiesChange(newEntities);
              }}
            >
              Select {count} {entityLabel}
            </Button>
          </div>
        );
      })()}

      {/* Create Group Dialog */}
      <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle>Create Group</DialogTitle>
            <DialogDescription className="!mt-2">
              Enter a name for the new group containing {selectedIds.filter(id => !String(id).startsWith('group-')).length} organizations.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {(() => {
              const selectedMembers = selectedIds.filter(id => !String(id).startsWith('group-'));
              const affectedGroups = groups.filter(g => g.memberIds.some(mId => selectedMembers.includes(mId)));
              
              if (affectedGroups.length > 0) {
                return (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 text-xs flex flex-col gap-1">
                    <span className="font-semibold">Note: Moving organizations</span>
                    <span>Some organizations already belong to existing groups: <span className="font-semibold">{affectedGroups.map(g => g.name).join(", ")}</span>. They will be moved to this new group.</span>
                  </div>
                );
              }
              return null;
            })()}
            <div className="grid items-center gap-4">
              <Input
                id="name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g., Ivy League"
                className="w-full"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmCreateGroup();
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGroupDialogOpen(false)}>Cancel</Button>
            <Button type="submit" onClick={confirmCreateGroup} className="bg-explore text-white hover:bg-explore/90">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalysisTable;
